import uuid from "uuid";
import { arrayMoveImmutable } from "array-move";
import { useState, useEffect } from "react";
import { SURVEY_TYPE } from "../../../../../api/surveys/config";
import { blockAdapter, getPathParams } from "../utilities";
import { BLOCK_FIELDS, DEFAULT_SURVEY_TITLE } from "../config";
import { useSurvey } from "../hooks/useSurvey";
import { useDebouncedState } from "../../../../../utilities/hooks/useDebouncedState";

var Id = () => uuid.v4();

export var DEFAULT_BLOCK_CHOICES = [""];

var DEFAULT_BLOCK = (type) => ({
  [BLOCK_FIELDS.id]: Id(),
  [BLOCK_FIELDS.type]: type || SURVEY_TYPE.text,
  [BLOCK_FIELDS.question]: "",
  [BLOCK_FIELDS.choices]: DEFAULT_BLOCK_CHOICES,
  [BLOCK_FIELDS.isRequired]: false,
  [BLOCK_FIELDS.isDescriptionRequired]: false,
  [BLOCK_FIELDS.description]: "",
  [BLOCK_FIELDS.commentsOnScale]: false,
});

var createSnapshot = (title, blocks, order) => {
  var order_ = order?.join(",") || "";

  var blocks_ = Array.from(blocks.entries())
    .flatMap(([_, block]) => Object.values(block))
    .join(",");

  return `${title}::${order_}::${blocks_}`;
};

export const useHandler = () => {
  const { isEditMode } = getPathParams();
  const survey = useSurvey();

  const [title, setTitle] = useState(DEFAULT_SURVEY_TITLE);
  const [focusedBlockId, setFocusedBlockId] = useState(undefined);

  const [blocks, setBlocks] = useState(new Map());
  const [order, setOrder] = useState([]);

  const [, snapshot, setSnapshot] = useDebouncedState("");

  useEffect(() => {
    if (blocks.size !== 0) return;

    if (!isEditMode) {
      const initialBlock = DEFAULT_BLOCK();
      const blocks = new Map([[initialBlock.id, initialBlock]]);
      const order = [initialBlock.id];
      setBlocks(blocks);
      setFocusedBlockId(initialBlock.id);
      setOrder(order);
      setSnapshot(createSnapshot(DEFAULT_SURVEY_TITLE, blocks, order));
    } else if (!survey.isLoading && !survey.isError) {
      const order = survey.data?.questions?.map((block) => block.id) || [];
      const blocks = new Map(
        survey.data.questions.map((block) => [
          block.id,
          blockAdapter.receive(block),
        ]),
      );
      const title = survey.data.title;

      setOrder(order);
      setTitle(title);
      setFocusedBlockId(survey.data.questions[0]?.id);
      setBlocks(blocks);

      setSnapshot(createSnapshot(title, blocks, order));
    }
  }, [blocks.size, isEditMode, survey.isLoading]);

  return {
    isLoading: isEditMode ? survey.isLoading : false,

    isError: isEditMode ? survey.isError : false,

    isPublished: isEditMode ? Boolean(survey.data?.isPublished) : false,

    title: {
      value: title,
      set: (newTitle) => {
        setTitle(newTitle);
      },
    },

    focusedBlock: {
      id: focusedBlockId,
      set: (id) => {
        setFocusedBlockId(id);
      },
    },

    blocks: {
      unwrap: () => {
        return order.map((id) => blocks.get(id));
      },

      create: (type) => {
        const newBlock = DEFAULT_BLOCK(type);
        blocks.set(newBlock.id, newBlock);
        setBlocks(new Map(blocks));
        setOrder((prevOrder) => [...prevOrder, newBlock.id]);
        setFocusedBlockId(newBlock.id);
      },

      update: (id, fieldType, value) => {
        const block = blocks.get(id);
        if (block) {
          block[fieldType] = value;
          blocks.set(id, block);
          setBlocks(new Map(blocks));
        }
      },

      getCurrent: () => {
        return blocks.get(focusedBlockId) || {};
      },

      getCurrentIndex: () => {
        const block = blocks.get(focusedBlockId) || {};
        return order.findIndex((id) => id === block.id);
      },

      delete: (id) => {
        if (blocks.has(id) && blocks.size > 1) {
          blocks.delete(id);
          setBlocks(new Map(blocks));

          const nextOrder = order.filter((blockId) => blockId !== id);
          setOrder(nextOrder);
          if (focusedBlockId === id) {
            setFocusedBlockId(nextOrder[nextOrder.length - 1]);
          }
        }
      },

      duplicate: (id) => {
        const block = blocks.get(id);
        if (block) {
          const newBlock = {
            ...block,
            id: Id(),
          };
          blocks.set(newBlock.id, newBlock);
          setBlocks(new Map(blocks));
          setOrder((prevOrder) => [...prevOrder, newBlock.id]);
          setFocusedBlockId(newBlock.id);
        }
      },

      reorder: (oldIndex, newIndex) => {
        setOrder(arrayMoveImmutable(order, oldIndex, newIndex));
      },

      isRequired: (id) => {
        const block = blocks.get(id);
        return block ? block.isRequired : false;
      },

      focusNextBlock: () => {
        const currentIndex = order.indexOf(focusedBlockId);
        if (currentIndex < order.length - 1) {
          setFocusedBlockId(order[currentIndex + 1]);
        }
      },
    },

    isStateChanged: () => {
      return snapshot !== createSnapshot(title, blocks, order);
    },
  };
};
